<template>
  <div class="pricing-comparison">
    <div class="billing-control">
      <span class="billing-label">Billing</span>
      <div class="billing-toggle" role="group" aria-label="Choose a billing period">
        <button
          type="button"
          :class="{ active: billingCycle === 'monthly' }"
          :aria-pressed="billingCycle === 'monthly'"
          @click="setBillingCycle('monthly')"
        >
          Monthly
        </button>
        <button
          type="button"
          :class="{ active: billingCycle === 'yearly' }"
          :aria-pressed="billingCycle === 'yearly'"
          @click="setBillingCycle('yearly')"
        >
          Yearly <span>2 months free</span>
        </button>
      </div>
    </div>

    <div class="mobile-plan-picker" role="group" aria-label="Choose a plan to compare">
      <button
        v-for="plan in plans"
        :key="plan.key"
        type="button"
        :class="{ active: mobilePlan === plan.key }"
        :aria-pressed="mobilePlan === plan.key"
        aria-controls="pricing-comparison-table"
        @click="setMobilePlan(plan.key)"
      >
        {{ plan.name }}
      </button>
    </div>

    <div
      :class="['pricing-table-scroll', `mobile-plan-${mobilePlan}`]"
      aria-label="MoDeck plan comparison"
    >
      <table id="pricing-comparison-table" class="plan-table">
        <caption class="sr-only">
          Compare MoDeck Free Trial, Pro, and Enterprise pricing and features.
        </caption>
        <thead>
          <tr>
            <th scope="col" class="feature-column plan-table-corner">
              <span class="corner-title">Compare plans</span>
              <span class="corner-note">Prices in USD, excluding tax</span>
            </th>
            <th
              v-for="plan in plans"
              :key="plan.key"
              scope="col"
              :class="['plan-column', `plan-column--${plan.key}`]"
            >
              <div class="plan-heading">
                <h3>{{ plan.name }}</h3>
                <p class="plan-summary">{{ plan.summary }}</p>
                <div class="plan-price" :aria-label="priceLabel(plan)">
                  <span class="currency">$</span>
                  <span class="amount">{{ activePrice(plan) }}</span>
                  <span class="period">/{{ activePeriod(plan) }}</span>
                </div>
                <p class="billing-note">{{ billingNote(plan) }}</p>
                <a
                  :href="plan.link"
                  class="plan-button"
                  :data-analytics-id="`pricing_${plan.key}`"
                  @click="selectPlan(plan)"
                >{{ plan.action }}</a>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="feature in features" :key="feature.key" class="feature-row">
            <th scope="row" class="feature-column">
              <span>{{ feature.label }}</span>
              <small v-if="feature.note">{{ feature.note }}</small>
            </th>
            <td
              v-for="plan in plans"
              :key="`${feature.key}-${plan.key}`"
              :class="`plan-cell--${plan.key}`"
            >
              <template v-if="feature[plan.key] === true">
                <span class="feature-check" aria-hidden="true">✓</span>
                <span class="sr-only">Included</span>
              </template>
              <template v-else-if="feature[plan.key] === false">
                <span class="feature-unavailable" aria-hidden="true">—</span>
                <span class="sr-only">Not included</span>
              </template>
              <span v-else class="feature-value">{{ feature[plan.key] }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pricing-notes">
      <p>
        The Free Trial lets you create and render templates for 15 days. Pro and
        Enterprise subscriptions can be billed monthly or yearly.
      </p>
      <p>
        API calls use account credits on the Free Trial and Pro plans. Enterprise API
        usage is unlimited, subject to fair-use request rate limits.
      </p>
      <p>
        Enterprise includes unlimited local rendering. A single CSV or Google Sheets
        batch accepts up to 100,000 rows; your hardware determines how quickly it finishes.
        <router-link to="/docs#data-driven">See data and batch limits.</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { trackEvent } from "../utils/analytics";

export default {
  name: "PricingOne",
  data() {
    return {
      billingCycle: "monthly",
      mobilePlan: "trial",
      plans: [
        {
          key: "trial",
          name: "Free Trial",
          summary: "Build and test your first workflow",
          prices: { monthly: "0", yearly: "0" },
          link: "https://app.modeck.io/login?signup=1",
          action: "Start free",
        },
        {
          key: "pro",
          name: "Pro",
          summary: "For individuals and smaller teams",
          prices: { monthly: "19", yearly: "190" },
          link: "https://app.modeck.io/settings?s=1",
          action: "Choose Pro",
        },
        {
          key: "enterprise",
          name: "Enterprise",
          summary: "For larger, branded workflows",
          prices: { monthly: "79", yearly: "790" },
          link: "https://app.modeck.io/settings?s=1",
          action: "Choose Enterprise",
        },
      ],
      features: [
        {
          key: "templates",
          label: "Templates",
          trial: "2",
          pro: "5",
          enterprise: "Unlimited",
        },
        {
          key: "editors",
          label: "Invited editors per template",
          trial: "1",
          pro: "3",
          enterprise: "Unlimited",
        },
        {
          key: "mogrts",
          label: "MOGRTs per template",
          trial: "3",
          pro: "8",
          enterprise: "Unlimited",
        },
        {
          key: "data-batch",
          label: "Videos per data-driven batch",
          note: "CSV or Google Sheets rows",
          trial: "3",
          pro: "30",
          enterprise: "100,000",
        },
        {
          key: "desktop-apps",
          label: "Connected desktop render apps",
          note: "Signed in to the same owner account",
          trial: "1",
          pro: "4",
          enterprise: "8",
        },
        {
          key: "browser-editors",
          label: "Sequence and Journey browser editors",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "local-rendering",
          label: "Automated rendering through After Effects",
          note: "Runs on your connected machine",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "template-logic",
          label: "Template logic and predefined sequences",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "scheduled-renders",
          label: "Scheduled data-driven renders",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "text-to-speech",
          label: "Text-to-speech generation",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "api-access",
          label: "API access",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "email-invites",
          label: "Template sharing by email invitation",
          trial: true,
          pro: true,
          enterprise: true,
        },
        {
          key: "unlimited-speech",
          label: "Unlimited text-to-speech usage",
          trial: false,
          pro: true,
          enterprise: true,
        },
        {
          key: "json-array",
          label: "Multi-video batches from JSON arrays",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "unlimited-api",
          label: "Unlimited API usage",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "user-groups",
          label: "Reusable editor groups",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "magic-links",
          label: "Magic Links for account-free editing",
          note: "Includes render-request approval controls",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "embedding",
          label: "Editor embedding on your own site",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "editor-branding",
          label: "Custom editor colors and logo",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "email-sender",
          label: "Send notifications from your own address",
          note: "Uses your SMTP account",
          trial: false,
          pro: false,
          enterprise: true,
        },
        {
          key: "email-content",
          label: "Fully custom notification emails",
          trial: false,
          pro: false,
          enterprise: true,
        },
      ],
    };
  },
  methods: {
    setBillingCycle(cycle) {
      if (this.billingCycle === cycle) return;
      this.billingCycle = cycle;
      trackEvent("pricing_billing_change", { billing_period: cycle === "yearly" ? "year" : "month" });
    },
    setMobilePlan(planKey) {
      if (this.mobilePlan === planKey) return;
      this.mobilePlan = planKey;
      trackEvent("pricing_plan_compare", { plan_name: planKey });
    },
    selectPlan(plan) {
      const analyticsBillingPeriod = this.billingCycle === "yearly" ? "year" : "month";
      trackEvent("select_item", {
        item_list_id: "landing_pricing",
        item_list_name: "Landing page pricing",
        plan_name: plan.key,
        billing_period: analyticsBillingPeriod,
        items: [
          {
            item_id: `${plan.key}_${analyticsBillingPeriod}`,
            item_name: `MoDeck ${plan.name}`,
            item_category: "Subscription",
            item_variant: analyticsBillingPeriod,
            price: Number(this.activePrice(plan)),
            quantity: 1,
          },
        ],
      });
    },
    activePrice(plan) {
      return plan.prices[this.billingCycle];
    },
    activePeriod(plan) {
      if (plan.key === "trial") return "15 days";
      return this.billingCycle === "monthly" ? "month" : "year";
    },
    billingNote(plan) {
      if (plan.key === "trial") return "No card required";
      return this.billingCycle === "monthly"
        ? "Billed monthly"
        : "Billed yearly · save 2 months";
    },
    priceLabel(plan) {
      if (plan.key === "trial") return "Free for 15 days";
      return `${this.activePrice(plan)} dollars per ${this.activePeriod(plan)}`;
    },
  },
};
</script>

<style scoped>
.pricing-comparison {
  --ink: #364563;
  --copy: #5a6e8e;
  --muted: #8290a7;
  --line: #dfe5ed;
  --soft: #f7f9fc;
  --accent: #d9317c;
  --aqua: #68c0ce;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.billing-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: -18px 0 30px;
}

.billing-label {
  color: var(--copy);
  font-size: 13px;
  font-weight: 600;
}

.billing-toggle {
  display: inline-flex;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--soft);
}

.billing-toggle button {
  min-height: 40px;
  padding: 0 17px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--copy);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.billing-toggle button span {
  margin-left: 4px;
  color: inherit;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.8;
}

.billing-toggle button.active {
  background: var(--ink);
  box-shadow: 0 4px 12px rgba(54, 69, 99, 0.16);
  color: #ffffff;
}

.mobile-plan-picker {
  display: none;
}

.billing-toggle button:focus-visible,
.mobile-plan-picker button:focus-visible,
.plan-button:focus-visible {
  outline: 3px solid rgba(104, 192, 206, 0.45);
  outline-offset: 3px;
}

.pricing-table-scroll {
  overflow-x: auto;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(54, 69, 99, 0.08);
  -webkit-overflow-scrolling: touch;
}

.plan-table {
  width: 100%;
  min-width: 900px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  color: var(--copy);
}

.plan-table th,
.plan-table td {
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.plan-table tr > :last-child {
  border-right: 0;
}

.plan-table tbody tr:last-child > * {
  border-bottom: 0;
}

.feature-column {
  width: 34%;
  text-align: left;
}

.plan-table-corner {
  padding: 30px 28px;
  vertical-align: bottom;
  background: var(--soft);
}

.corner-title,
.corner-note {
  display: block;
}

.corner-title {
  margin-bottom: 6px;
  color: var(--ink);
  font-size: 18px;
  font-weight: 600;
}

.corner-note {
  color: var(--muted);
  font-size: 12px;
  font-weight: 400;
}

.plan-column {
  width: 22%;
  padding: 0;
  vertical-align: top;
  background: #ffffff;
  text-align: center;
}

.plan-column--pro {
  background: #fbfcfe;
}

.plan-column--enterprise {
  background: #f4fbfc;
}

.plan-heading {
  display: flex;
  min-height: 292px;
  flex-direction: column;
  align-items: center;
  padding: 28px 18px 24px;
}

.plan-heading h3 {
  margin: 0 0 8px;
  color: var(--ink);
  font-size: 21px;
  font-weight: 600;
}

.plan-summary {
  min-height: 42px;
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
  font-weight: 400;
}

.plan-price {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 61px;
  margin-top: 22px;
  color: var(--ink);
}

.plan-price .currency {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
}

.plan-price .amount {
  margin: 0 4px;
  font-size: 43px;
  font-weight: 300;
  letter-spacing: -0.045em;
  line-height: 1;
}

.plan-price .period {
  align-self: flex-end;
  margin-bottom: 7px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.billing-note {
  min-height: 19px;
  margin: 3px 0 19px;
  color: var(--muted);
  font-size: 11px;
}

.plan-button {
  display: inline-flex;
  min-width: 142px;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding: 9px 18px;
  border: 1px solid var(--ink);
  border-radius: 5px;
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    transform 0.2s ease;
}

.plan-column--pro .plan-button {
  border-color: var(--accent);
  background: var(--accent);
  color: #ffffff;
}

.plan-column--enterprise .plan-button {
  border-color: var(--aqua);
  color: #317a86;
}

.plan-button:hover {
  border-color: var(--accent);
  background: var(--accent);
  color: #ffffff;
  transform: translateY(-1px);
}

.feature-row th,
.feature-row td {
  min-height: 58px;
  padding: 14px 20px;
}

.feature-row th {
  padding-left: 28px;
  background: #ffffff;
  color: var(--ink);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.feature-row th span,
.feature-row th small {
  display: block;
}

.feature-row th small {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 400;
  line-height: 1.45;
}

.feature-row td {
  background: #ffffff;
  color: var(--copy);
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  vertical-align: middle;
}

.feature-row td.plan-cell--pro {
  background: #fbfcfe;
}

.feature-row td.plan-cell--enterprise {
  background: #f4fbfc;
}

.feature-check {
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(104, 192, 206, 0.16);
  color: #317a86;
  font-size: 15px;
  font-weight: 700;
}

.feature-unavailable {
  color: #bec6d2;
  font-size: 17px;
  font-weight: 400;
}

.feature-value {
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
}

.pricing-notes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  max-width: 980px;
  margin: 24px auto 0;
}

.pricing-notes p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.65;
}

@media (max-width: 991px) {
  .mobile-plan-picker {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-width: 560px;
    padding: 4px;
    margin: 0 auto 18px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--soft);
  }

  .mobile-plan-picker button {
    min-height: 42px;
    padding: 7px 10px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--copy);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-plan-picker button.active {
    background: #ffffff;
    box-shadow: 0 3px 12px rgba(54, 69, 99, 0.12);
    color: var(--ink);
  }

  .pricing-table-scroll {
    overflow-x: hidden;
    margin-right: -15px;
    margin-left: -15px;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  .plan-table {
    min-width: 0;
  }

  .plan-column,
  .feature-row td {
    display: none;
  }

  .mobile-plan-trial .plan-column--trial,
  .mobile-plan-trial .plan-cell--trial,
  .mobile-plan-pro .plan-column--pro,
  .mobile-plan-pro .plan-cell--pro,
  .mobile-plan-enterprise .plan-column--enterprise,
  .mobile-plan-enterprise .plan-cell--enterprise {
    display: table-cell;
    border-right: 0;
  }

  .feature-column {
    width: 58%;
  }

  .plan-column {
    width: 42%;
  }

  .plan-heading {
    min-height: 274px;
  }

  .plan-summary {
    min-height: 38px;
  }
}

@media (max-width: 767px) {
  .billing-control {
    flex-direction: column;
    gap: 8px;
    margin-top: -12px;
  }

  .billing-label {
    display: none;
  }

  .billing-toggle button {
    padding: 0 14px;
  }

  .feature-column {
    width: 56%;
  }

  .plan-column {
    width: 44%;
  }

  .plan-heading {
    min-height: 276px;
    padding-right: 12px;
    padding-left: 12px;
  }

  .plan-summary {
    min-height: 56px;
  }

  .plan-price .amount {
    font-size: 36px;
  }

  .plan-button {
    min-width: 125px;
    padding-right: 12px;
    padding-left: 12px;
  }

  .feature-row th,
  .plan-table-corner {
    padding-left: 20px;
  }

  .pricing-notes {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

@media (max-width: 420px) {
  .billing-toggle button span {
    display: none;
  }

  .mobile-plan-picker {
    margin-right: -4px;
    margin-left: -4px;
  }

  .mobile-plan-picker button {
    padding-right: 5px;
    padding-left: 5px;
    font-size: 11px;
  }

  .feature-column {
    width: 57%;
  }

  .plan-column {
    width: 43%;
  }

  .plan-heading {
    min-height: 262px;
    padding: 24px 9px 20px;
  }

  .plan-heading h3 {
    font-size: 19px;
  }

  .plan-price .amount {
    font-size: 32px;
  }

  .plan-button {
    width: 100%;
    min-width: 0;
  }

  .feature-row th,
  .feature-row td {
    padding-right: 12px;
    padding-left: 12px;
  }

  .feature-row th {
    font-size: 13px;
  }
}
</style>
